import { X } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useId, useRef } from "react";

import { cn } from "./lib/cn.js";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  className?: string;
  /**
   * Placement. `"center"` (default) is the classic centred modal; `"right"` is
   * a full-height slide-over drawer pinned to the right edge. Both reuse the
   * native <dialog> a11y (focus trap, Escape, top-layer backdrop). Backdrop
   * clicks do not dismiss — Escape and × are the intentional exits.
   */
  side?: "center" | "right";
}

/**
 * Modal dialog wrapper around the native <dialog> element. Why this shape
 * and not `display:flex` on the dialog: Chromium's UA stylesheet centers
 * the dialog via `margin: auto` + `inset: 0`, which gets confused when
 * the dialog itself is a flex container — we saw this surface as a
 * stuck-to-top-left modal that the user couldn't dismiss. Keep `<dialog>`
 * at default block display and put scroll/flex layout on an inner
 * wrapper.
 */
export function Dialog({
  open,
  onClose,
  title,
  children,
  className,
  side = "center",
}: DialogProps) {
  const isDrawer = side === "right";
  const dialogRef = useRef<HTMLDialogElement>(null);
  const ignoredCloseEvents = useRef(0);
  const titleId = useId();

  const closeWithoutNotify = useCallback((dialog: HTMLDialogElement) => {
    ignoredCloseEvents.current += 1;
    dialog.close();
  }, []);

  const handleNativeClose = useCallback(() => {
    if (ignoredCloseEvents.current > 0) {
      ignoredCloseEvents.current -= 1;
      return;
    }

    onClose();
  }, [onClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      if (!dialog.open) dialog.showModal();
    } else {
      if (dialog.open) closeWithoutNotify(dialog);
    }
  }, [closeWithoutNotify, open]);

  // Cleanup on unmount: if the parent that opened us unmounts (e.g.
  // route change), make sure we close the dialog so it doesn't leak
  // into the top layer of the next page. React dev StrictMode also
  // runs this cleanup immediately after mount; that lifecycle close
  // must not notify the parent, or the modal opens and instantly
  // unmounts in local development.
  useEffect(() => {
    const dialog = dialogRef.current;
    return () => {
      if (dialog?.open) closeWithoutNotify(dialog);
    };
  }, [closeWithoutNotify]);

  return (
    // The <dialog> element is natively interactive (Escape closes,
    // focus is trapped). Backdrop clicks deliberately do NOT dismiss —
    // a slight miss while aiming at a control was wiping in-progress
    // forms across the product (canon 01-ui/dialog.md R5).
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      onClose={handleNativeClose}
      // Native <dialog> + showModal() relies on the UA stylesheet's
      // `position: fixed; inset: 0; margin: auto` triplet to centre.
      // Tailwind v4's Preflight resets `margin: 0` on all elements
      // (including <dialog>), which silently kills that centring and
      // pins the modal to top-left. Restore the centring explicitly.
      //
      // Width: `w-[calc(100vw-2rem)]` makes the dialog fill the
      // viewport minus a 1rem gutter; `max-w-lg` is the default cap.
      // Callers override the cap by passing `max-w-2xl` / `max-w-5xl`
      // / etc. via `className` — twMerge resolves `max-w-*` against
      // `max-w-*` so the override actually takes effect. We avoided
      // the previous `w-[min(32rem,calc(100vw-2rem))]` because `w-*`
      // and `max-w-*` are different property groups; twMerge does
      // not resolve them together, so any caller `max-w-*` was
      // silently dead and every modal capped at 32rem regardless of
      // intent.
      className={cn(
        // Drawer: full-height slide-over pinned right (corners rounded on the
        // inner edge only). Centre: the classic modal. Both restore the
        // centring/positioning Tailwind v4 Preflight strips off <dialog>.
        isDrawer
          ? "fixed inset-y-0 right-0 m-0 h-full max-h-screen w-[calc(100vw-2rem)] max-w-md rounded-l-soft-lg rounded-r-none"
          : "fixed inset-0 m-auto h-fit w-[calc(100vw-2rem)] max-w-lg max-h-[90vh] rounded-soft-lg",
        "bg-surface p-0 shadow-xl backdrop:bg-black/50 overflow-hidden",
        className,
      )}
    >
      <div className={cn("flex flex-col", isDrawer ? "h-full" : "max-h-[90vh]")}>
        <div className="flex shrink-0 items-center justify-between border-b px-6 py-4">
          <h2 id={titleId} className="text-lg font-semibold text-fg">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-fg-subtle hover:text-fg-muted"
            aria-label="Close"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        <div className={cn("overflow-y-auto px-6 py-4", isDrawer && "flex-1")}>{children}</div>
      </div>
    </dialog>
  );
}
